import PropTypes from 'prop-types'
import React, { Component } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { Button } from 'components/styled/button'
import { mapLanguageToComponentProps } from 'i18n'
import Video from 'components/video/video'
import VideoPlaceholder from 'components/video-placeholder/video-placeholder'
import VideoUpload from 'components/video-upload/video-upload'
import Webcam from 'components/webcam/webcam'
import videoServices from 'services/video'
import {
  setOpenQuestion,
  toggleRecordingState,
  updateVideoResponse,
} from 'actions/video-response'
import { Subtitle } from 'components/styled/typography'
import { addError } from 'actions'

const propTypes = {
  response: PropTypes.shape({
    videoUrl: PropTypes.string,
    playbackVideoUrl: PropTypes.string,
    has: PropTypes.bool,
    recorder: PropTypes.string,
    saved: PropTypes.bool,
    backgounrd: PropTypes.string,
    blurAmount: PropTypes.number,
  }),
  question: PropTypes.shape({
    responseVideoUrl: PropTypes.string,
    required: PropTypes.bool,
    _id: PropTypes.string,
  }),
  context: PropTypes.oneOf(['application', 'candidate', 'messages']),
  onHeightChange: PropTypes.func,
  onVideoSubmit: PropTypes.func,
  onVideoChange: PropTypes.func,
  onVideoRemove: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  useFileUpload: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  uuid: PropTypes.string,
  i18n: PropTypes.shape({
    videoResponse: PropTypes.object,
  }).isRequired,
  disableRecording: PropTypes.bool,
  publisher: PropTypes.shape({}),
  archive: PropTypes.shape({}),
  streamStarting: PropTypes.bool,
  recording: PropTypes.bool,
  globalRecorder: PropTypes.string,
  openQuestion: PropTypes.string,
  bgOptions: PropTypes.shape({
    background: PropTypes.string,
    blurAmount: PropTypes.number,
  }),
  videoEventProps: PropTypes.shape({}),
  locationId: PropTypes.string,
  allowUploadWithRecord: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  createDraftCandidate: PropTypes.func,
  showRequiredWarning: PropTypes.bool,
}

const defaultProps = {
  onHeightChange: () => {},
  onVideoSubmit: undefined,
  onVideoChange: () => {},
  onVideoRemove: () => {},
  question: undefined,
  response: undefined,
  context: undefined,
  useFileUpload: false,
  uuid: undefined,
  className: '',
  type: '',
  disableRecording: false,
  publisher: undefined,
  archive: undefined,
  streamStarting: false,
  recording: false,
  allowUploadWithRecord: false,
  globalRecorder: '',
  openQuestion: undefined,
  bgOptions: undefined,
  videoEventProps: undefined,
  locationId: '',
  isEmbedded: false,
  createDraftCandidate: undefined,
  showRequiredWarning: false,
}

const mapStateToProps = state => ({
  publisher: state.global.opentok.publisher,
  streaming: state.global.opentok.streaming,
  archive: state.global.opentok.archive,
  streamStarting: state.global.opentok.streamStarting,
  recording: state.global.recording,
  globalRecorder: state.global.recorder,
  openQuestion: state.global.openQuestion,
  bgOptions: state.global.bgOptions,
})

const VideoContainer = styled.div`
  height: 100%;
  width: 100%;
`

export class VideoResponse extends Component {
  constructor(props) {
    super(props)
    this.state = {
      recordingView: false,
      upload: props.useFileUpload,
    }
    this.refreshPlaybackUrl = this.refreshPlaybackUrl.bind(this)
  }

  componentDidMount() {
    const {
      context,
      response,
      question,
      onHeightChange,
      dispatch,
      disableRecording,
    } = this.props
    const videoUrl = question.responseVideoUrl
    const playbackVideoUrl = question.responsePlaybackUrl

    onHeightChange()

    if (
      !disableRecording &&
      (typeof response === 'undefined' || !response.videoUrl)
    ) {
      dispatch(
        updateVideoResponse(context, response, {
          videoUrl,
          playbackVideoUrl,
          has: false,
          questionId: question._id,
        })
      )
    } else {
      this.setState({ recordingView: response && response.has && !this.state.upload })
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { response, onHeightChange, onVideoChange, onVideoRemove } =
      this.props
    if (
      prevState.recordingView !== this.state.recordingView ||
      (response &&
        prevProps.response &&
        response.saved !== prevProps.response.saved)
    ) {
      onHeightChange()
    }
    if (
      response !== prevProps.response &&
      (typeof response === 'undefined' || !response.videoUrl)
    ) {
      this.fetchVideoUrl()
    }

    if (
      prevProps.response &&
      response &&
      prevProps.response.has !== response.has
    ) {
      response.has ? onVideoChange() : onVideoRemove()
    }
  }

  fetchVideoUrl() {
    const { context, response, question, dispatch } = this.props

    videoServices
      .fetchUrl()
      .then(({ videoUrl }) => {
        dispatch(
          updateVideoResponse(context, response, {
            videoUrl,
            has: false,
            questionId: question._id,
          })
        )
      })
      .catch(err => dispatch(addError(err)))
  }

  updateVideo(data) {
    const { context, response, dispatch } = this.props
    dispatch(updateVideoResponse(context, response, data))
  }

  removeVideo() {
    this.updateVideo({ has: false })
    this.cancelRecording()
  }

  startRecording() {
    const { dispatch, response } = this.props
    if (response) {
      dispatch(setOpenQuestion(response.questionId))
    }
    dispatch(toggleRecordingState(true))
    this.setState(() => ({
      recordingView: true,
    }))
  }

  cancelRecording() {
    const { dispatch } = this.props
    dispatch(toggleRecordingState(false))
    this.setState(() => ({
      recordingView: false,
    }))
  }

  refreshPlaybackUrl(useOpentokPreview) {
    const { response } = this.props
    return videoServices.fetchPlaybackUrl(response, useOpentokPreview)
  }

  render() {
    const {
      context,
      i18n,
      onVideoSubmit,
      response,
      className,
      uuid,
      type,
      disableRecording,
      question,
      streamStarting,
      recording,
      archive,
      globalRecorder,
      openQuestion,
      bgOptions,
      videoEventProps,
      locationId,
      allowUploadWithRecord,
      isEmbedded,
      createDraftCandidate,
      showRequiredWarning,
    } = this.props
    const { upload, recordingView } = this.state
    const archivingOn = archive && archive.status === 'started'

    const useUpload = upload || globalRecorder === 'upload'

    const recordButton = (
      <Button
        icon="radio_button_checked"
        disabled={
          disableRecording || streamStarting || archivingOn || recording
        }
        text={
          useUpload
            ? i18n.videoResponse.uploadPlaceholder
            : i18n.videoResponse.videoPlaceholder
        }
        onClick={() => this.startRecording()}
      />
    )

    if (
      context === 'candidate' &&
      isEmbedded &&
      !uuid &&
      createDraftCandidate
    ) {
      return (
        <VideoContainer className={`video-response ${className}`}>
          <VideoPlaceholder>
            <Button
              onClick={createDraftCandidate}
              iconRight="east"
              disabled={disableRecording}
              text={i18n.jobAdApplication.continue}
            />
          </VideoPlaceholder>
        </VideoContainer>
      )
    }

    if (context !== 'application' && response && response.saved) {
      return (
        <div className={`video-response ${className}`}>
          <Video
            id={response._id}
            videoUrl={response.videoUrl}
            playbackVideoUrl={response.playbackVideoUrl}
            recorder={response.recorder}
            refreshPlaybackUrl={useOpentokPreview =>
              this.refreshPlaybackUrl(useOpentokPreview)
            }
            locationId={locationId}
            eventId="videoResponsePlayButton"
            videoEventProps={videoEventProps}
          />
        </div>
      )
    }

    if (
      recordingView ||
      (response && response.has && !useUpload && !allowUploadWithRecord)
    ) {
      return (
        <VideoContainer className={`video-response ${className}`}>
          <Webcam
            uuid={uuid}
            className={className}
            type={type}
            context={context}
            mode="interview"
            video={response}
            onClose={() => this.cancelRecording()}
            onError={() => this.cancelRecording()}
            onRemove={() => this.removeVideo()}
            onVideoSubmit={onVideoSubmit}
            updateVideo={video => this.updateVideo(video)}
            responseVideoUrl={question.responseVideoUrl}
            disableRecording={disableRecording}
            openQuestion={openQuestion}
            bgOptions={bgOptions}
            videoEventProps={videoEventProps}
            locationId={locationId}
            isEmbedded={isEmbedded}
          />
        </VideoContainer>
      )
    }

    if (useUpload || allowUploadWithRecord) {
      const renderRecord = !useUpload && allowUploadWithRecord
      return (
        <VideoContainer className={`video-response ${className}`}>
          <VideoUpload
            className={className}
            video={response}
            updateVideo={video => this.updateVideo(video)}
            onRemove={() => this.removeVideo()}
            onVideoSubmit={onVideoSubmit}
            disableUpload={disableRecording}
            context={context}
            type={type}
            pollId={uuid}
            isEmbedded={isEmbedded}
            videoUploadEvent={{
              locationId,
              id: 'videoUploadButton',
              properties: videoEventProps,
            }}
            recordButton={renderRecord ? recordButton : undefined}
          />
        </VideoContainer>
      )
    }

    return (
      <VideoContainer className={`video-response ${className}`}>
        <VideoPlaceholder>
          <Subtitle className="notanswered">
            {i18n.videoResponse.notAnswered}
          </Subtitle>
          {recordButton}
          {question && question.required && (
            <Subtitle
              className="notanswered"
              color={showRequiredWarning ? 'error110' : undefined}
            >
              {i18n.videoResponse.required}
            </Subtitle>
          )}
        </VideoPlaceholder>
      </VideoContainer>
    )
  }
}

VideoResponse.propTypes = propTypes
VideoResponse.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  VideoResponse
)
